import { useState } from 'react';
import {
  createGoal,
  deleteGoal,
  getFinanceOverview,
  getGoals,
  updateGoal,
  type Goal,
  type GoalStatus,
  type GoalWithProgress,
} from '../api.js';
import { Button, EmptyState, ErrorState, Sheet, useToast } from '../ui/index.js';
import { useLoad } from '../lifeos/useLoad.js';
import { errorText } from '../lifeos/toast.js';
import { parseDollarsToCents } from './Budget.js';
import { dollars, shortDate } from './format.js';
import './reports.css';

/**
 * Goals — the finance slice of the intent layer: goals with `domain='finance'`.
 * Metric goals score against the finance metrics in the shared hypertable
 * (`net_worth_usd`, `spend_usd`, `income_usd`), whose values are DOLLARS — so
 * the server's progress figures are dollars too, converted to cents only to be
 * formatted here.
 */

type PresetKey = 'emergency' | 'networth' | 'spendcap' | 'custom';

interface Preset {
  key: PresetKey;
  name: string;
  hint: string;
  title: string;
  metricType: 'net_worth_usd' | 'spend_usd' | null;
  direction: 'up' | 'down' | null;
}

export const PRESETS: readonly Preset[] = [
  {
    key: 'emergency',
    name: 'Emergency fund',
    hint: 'Grow net worth to a cushion — three to six months of spending.',
    title: 'Build an emergency fund',
    metricType: 'net_worth_usd',
    direction: 'up',
  },
  {
    key: 'networth',
    name: 'Net worth target',
    hint: 'Reach a net worth by a date.',
    title: 'Reach net worth target',
    metricType: 'net_worth_usd',
    direction: 'up',
  },
  {
    key: 'spendcap',
    name: 'Monthly spending cap',
    hint: 'Keep spending under a monthly amount. Tracked against daily spending (the cap ÷ 30).',
    title: 'Keep monthly spending under cap',
    metricType: 'spend_usd',
    direction: 'down',
  },
  { key: 'custom', name: 'Custom', hint: 'Your own title, target and date.', title: '', metricType: null, direction: null },
];

/** A metric value in dollars (the hypertable's unit) → cents, at the display edge. */
const cents = (usd: number | null | undefined): number | null => (usd == null ? null : Math.round(usd * 100));

const STATUS_TEXT: Record<string, { label: string; tone: 'good' | 'warn' | 'bad' | 'accent' | 'muted' }> = {
  achieved: { label: 'Achieved', tone: 'good' },
  ahead: { label: 'Ahead', tone: 'good' },
  on_track: { label: 'On track', tone: 'accent' },
  behind: { label: 'Behind', tone: 'warn' },
  unknown: { label: 'No data yet', tone: 'muted' },
};

/** The goal body the sheet sends. Exported for tests. */
export function buildGoalBody(input: {
  preset: Preset;
  title: string;
  targetCents: number;
  targetDate: string;
  baselineCents: number | null;
}): Partial<Goal> & { title: string } {
  const { preset, title, targetCents, targetDate, baselineCents } = input;
  const body: Partial<Goal> & { title: string } = { title: title.trim() || preset.title, domain: 'finance' };
  if (targetDate) body.targetDate = targetDate;
  const metricType = preset.metricType;
  if (!metricType || !preset.direction) {
    // A custom goal with a target but no metric: a milestone, the amount kept in the detail.
    body.kind = 'milestone';
    if (targetCents > 0) body.detail = `Target ${dollars(targetCents)}`;
    return body;
  }
  body.kind = 'metric';
  body.metricType = metricType;
  body.direction = preset.direction;
  if (metricType === 'spend_usd') {
    // spend_usd is one value per day, so a monthly cap is scored as its daily share.
    body.targetValue = Math.round(targetCents / 30) / 100;
    body.detail = `Monthly cap ${dollars(targetCents)} (≈ ${dollars(Math.round(targetCents / 30))}/day)`;
  } else {
    body.targetValue = targetCents / 100;
    if (baselineCents !== null) body.baselineValue = baselineCents / 100;
  }
  return body;
}

export default function Goals(): JSX.Element {
  const goals = useLoad(async () => (await getGoals('all')).filter((g) => g.goal.domain === 'finance'), []);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<GoalWithProgress | null>(null);

  const list = goals.data ?? [];
  const open = list.filter((g) => g.goal.status === 'active' || g.goal.status === 'paused');
  const done = list.filter((g) => g.goal.status === 'achieved' || g.goal.status === 'abandoned');

  return (
    <div className="mr-page">
      <div className="mr-toolbar">
        <h2 className="mr-h2">Financial goals</h2>
        <span className="mr-month__spacer" />
        <Button size="sm" icon="plus" onClick={() => setAdding(true)}>
          New goal
        </Button>
      </div>

      {goals.error && !goals.data ? (
        <ErrorState message={goals.error} onRetry={() => void goals.reload()} />
      ) : !goals.data ? (
        <div className="mr-loading" aria-busy="true">
          Loading…
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="target"
          title="No money goals yet"
          hint="An emergency fund, a net-worth number, a spending cap — goals here track themselves from your accounts."
          action={
            <Button icon="plus" onClick={() => setAdding(true)}>
              New goal
            </Button>
          }
        />
      ) : (
        <>
          <div className="mr-goals">
            {open.map((g) => (
              <GoalCard key={g.goal.id} entry={g} onOpen={() => setEditing(g)} />
            ))}
          </div>
          {done.length > 0 && (
            <>
              <h3 className="mr-h3">Finished</h3>
              <div className="mr-goals mr-goals--done">
                {done.map((g) => (
                  <GoalCard key={g.goal.id} entry={g} onOpen={() => setEditing(g)} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {adding && (
        <NewGoalSheet
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            void goals.reload();
          }}
        />
      )}
      {editing && (
        <GoalSheet
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void goals.reload();
          }}
        />
      )}
    </div>
  );
}

function GoalCard({ entry, onOpen }: { entry: GoalWithProgress; onOpen: () => void }): JSX.Element {
  const { goal, metric, summary } = entry;
  const statusKey = goal.status === 'achieved' ? 'achieved' : (metric?.status ?? null);
  const status = statusKey ? STATUS_TEXT[statusKey] : null;
  const pct = metric ? Math.max(0, Math.min(1, metric.fraction)) : goal.status === 'achieved' ? 1 : 0;
  const isCap = goal.metricType === 'spend_usd';

  return (
    <button type="button" className="mr-card mr-goal" onClick={onOpen} aria-label={`Goal ${goal.title}`}>
      <span className="mr-goal__head">
        <span className="mr-goal__title">{goal.title}</span>
        {goal.status === 'paused' ? (
          <span className="hb-pill">Paused</span>
        ) : goal.status === 'abandoned' ? (
          <span className="hb-pill">Dropped</span>
        ) : (
          status && <span className={`hb-pill hb-pill--${status.tone === 'muted' ? 'accent' : status.tone}`}>{status.label}</span>
        )}
      </span>
      {metric && goal.targetValue != null && (
        <span className="mr-goal__figs">
          <span className="mr-goal__now">{dollars(cents(metric.currentValue))}</span>
          <span className="mr-muted">
            {isCap ? 'yesterday · cap ' : 'of '}
            {dollars(cents(goal.targetValue))}
            {isCap ? '/day' : ''}
          </span>
        </span>
      )}
      {(metric || goal.status === 'achieved') && !isCap && (
        <span className={`mr-progress mr-progress--${statusKey === 'behind' ? 'near' : 'ok'}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct * 100)}>
          <span className="mr-progress__fill mr-progress__fill--goal" style={{ width: `${Math.round(pct * 100)}%` }} />
        </span>
      )}
      <span className="mr-goal__sub">{summary || goal.detail}</span>
      {goal.targetDate && <span className="mr-goal__date">By {shortDate(goal.targetDate)}</span>}
    </button>
  );
}

function NewGoalSheet({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }): JSX.Element {
  const toast = useToast();
  const [presetKey, setPresetKey] = useState<PresetKey>('emergency');
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const preset = PRESETS.find((p) => p.key === presetKey)!;
  const targetCents = parseDollarsToCents(amount);
  const valid = targetCents !== null && targetCents > 0 && (presetKey !== 'custom' || title.trim().length > 0);

  const save = async (): Promise<void> => {
    if (!valid || targetCents === null) return;
    setBusy(true);
    try {
      // Net-worth goals start from today's net worth, so progress means distance covered.
      let baselineCents: number | null = null;
      if (preset.metricType === 'net_worth_usd') {
        baselineCents = await getFinanceOverview().then(
          (o) => o.netCents,
          () => null,
        );
      }
      await createGoal(buildGoalBody({ preset, title, targetCents, targetDate: date, baselineCents }));
      onSaved();
    } catch (err) {
      toast.error(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Sheet title="New goal" onClose={onClose} action={{ label: 'Add', onClick: () => void save(), disabled: !valid || busy }}>
      <form
        className="hb-form"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="mr-presets" role="radiogroup" aria-label="Goal type">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="radio"
              aria-checked={presetKey === p.key}
              className={`mr-preset${presetKey === p.key ? ' mr-preset--on' : ''}`}
              onClick={() => setPresetKey(p.key)}
            >
              <span className="mr-preset__name">{p.name}</span>
              <span className="mr-preset__hint">{p.hint}</span>
            </button>
          ))}
        </div>
        <label className="hb-field">
          <span>Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={preset.title || 'Pay off the car'} aria-label="Title" />
        </label>
        <div className="hb-form__row">
          <label className="hb-field">
            <span>{preset.key === 'spendcap' ? 'Monthly cap' : 'Target'}</span>
            <input inputMode="decimal" placeholder="$0" aria-label="Target amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
          <label className="hb-field">
            <span>By (optional)</span>
            <input type="date" aria-label="Target date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
        </div>
        {amount && targetCents === null && <p className="hb-form__error">Enter an amount like 10000 or 2500.50.</p>}
        <Button type="submit" size="lg" block disabled={!valid || busy}>
          {busy ? 'Adding…' : 'Add goal'}
        </Button>
      </form>
    </Sheet>
  );
}

function GoalSheet({ entry, onClose, onSaved }: { entry: GoalWithProgress; onClose: () => void; onSaved: () => void }): JSX.Element {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const { goal } = entry;

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      onSaved();
    } catch (err) {
      toast.error(errorText(err));
      setBusy(false);
    }
  };
  const setStatus = (status: GoalStatus) => run(() => updateGoal(goal.id, { status }));

  return (
    <Sheet title={goal.title} onClose={onClose}>
      <div className="hb-form">
        <p className="hb-hint">{entry.summary || goal.detail}</p>
        {goal.status !== 'achieved' && (
          <Button block disabled={busy} onClick={() => void setStatus('achieved')}>
            Mark achieved
          </Button>
        )}
        {goal.status === 'active' ? (
          <Button variant="secondary" block disabled={busy} onClick={() => void setStatus('paused')}>
            Pause
          </Button>
        ) : (
          <Button variant="secondary" block disabled={busy} onClick={() => void setStatus('active')}>
            {goal.status === 'paused' ? 'Resume' : 'Reopen'}
          </Button>
        )}
        <Button variant="destructive" block disabled={busy} onClick={() => void run(() => deleteGoal(goal.id))}>
          Delete goal
        </Button>
      </div>
    </Sheet>
  );
}
